import { createContext, useContext, ReactNode, useMemo } from "react";
import {
  useUserContext,
  useGameContext,
  useThemeContext,
  useStartupContext,
  useNotificationContext,
} from "../modules";

// Context类型定义
interface AppContextType {
  // 用户模块上下文
  user: ReturnType<typeof useUserContext>;
  // 游戏模块上下文
  game: ReturnType<typeof useGameContext>;
  // 主题模块上下文
  theme: ReturnType<typeof useThemeContext>;
  // 启动模块上下文
  startup: ReturnType<typeof useStartupContext>;
  // 通知模块上下文
  notification: ReturnType<typeof useNotificationContext>;
}

// 创建Context
const AppContext = createContext<AppContextType | null>(null);

// Provider Props
interface AppContextProviderProps {
  children: ReactNode;
}

// Provider组件
export function AppContextProvider({ children }: AppContextProviderProps) {
  const user = useUserContext();
  const game = useGameContext();
  const theme = useThemeContext();
  const startup = useStartupContext();
  const notification = useNotificationContext();

  const value: AppContextType = useMemo(
    () => ({
      user,
      game,
      theme,
      startup,
      notification,
    }),
    [user, game, theme, startup, notification]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// Hook
export function useAppContext(): AppContextType {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within AppContextProvider");
  }
  return context;
}

export default AppContext;
